/**
 * API Service pour les périmètres partagés
 */
import type {
    PerimetreShare,
    SharedPerimetreView,
    NoteSharePermission
} from '../../types/noteShare';

const API_BASE = '/api/notes-feed';

// Token d'authentification
let authToken: string | null = null;

/**
 * Configure le token d'authentification
 */
export function setAuthToken(token: string) {
    authToken = token;
}

/**
 * Helper pour les requêtes authentifiées
 */
async function fetchWithAuth<T>(url: string, options: RequestInit = {}): Promise<T> {
    if (!authToken) {
        throw new Error('Token d\'authentification non configuré');
    }

    const response = await fetch(url, {
        ...options,
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`,
            'accesstoken': authToken, // Required for Azure SWA
            ...options.headers
        }
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Erreur inconnue' }));
        throw new Error(error.message || `Erreur HTTP ${response.status}`);
    }

    const result = await response.json();
    return result.data !== undefined ? result.data : result;
}

/**
 * Récupère les périmètres que je partage (je suis propriétaire)
 */
export async function getMyPerimetreShares(): Promise<PerimetreShare[]> {
    return fetchWithAuth(`${API_BASE}/my-perimetre-shares`);
}

/**
 * Récupère les périmètres partagés avec moi
 */
export async function getSharedWithMePerimetres(): Promise<SharedPerimetreView[]> {
    return fetchWithAuth(`${API_BASE}/shared-perimetres`);
}

/**
 * Ajoute un participant à un périmètre partagé
 */
export async function addParticipant(
    perimetreId: string,
    perimetreName: string,
    participant: {
        userId: string;
        displayName: string;
        email?: string;
        avatar?: string;
        permission?: NoteSharePermission;
    }
): Promise<PerimetreShare> {
    return fetchWithAuth(`${API_BASE}/share-perimetre`, {
        method: 'POST',
        body: JSON.stringify({
            perimetreId,
            perimetreName,
            sharedWithUserId: participant.userId,
            sharedWithDisplayName: participant.displayName,
            sharedWithEmail: participant.email,
            sharedWithAvatar: participant.avatar,
            permission: participant.permission || 'read'
        })
    });
}

/**
 * Retire un participant d'un périmètre partagé
 */
export async function removeParticipant(
    perimetreShareId: string,
    participantUserId: string
): Promise<boolean> {
    return fetchWithAuth(`${API_BASE}/perimetre-participant`, {
        method: 'DELETE',
        body: JSON.stringify({
            perimetreShareId,
            participantUserId
        })
    });
}

/**
 * Met à jour la permission d'un participant
 */
export async function updateParticipantPermission(
    perimetreShareId: string,
    participantUserId: string,
    permission: NoteSharePermission
): Promise<PerimetreShare> {
    return fetchWithAuth(`${API_BASE}/perimetre-participant`, {
        method: 'PUT',
        body: JSON.stringify({
            perimetreShareId,
            participantUserId,
            permission
        })
    });
}

/**
 * Récupère un périmètre partagé par son ID
 */
export async function getPerimetreShareById(perimetreShareId: string): Promise<PerimetreShare> {
    return fetchWithAuth(`${API_BASE}/perimetre-share/${perimetreShareId}`);
}
